<template>
  <div class="space-y-4">
    <!-- Filters bar -->
    <div class="bg-white p-4 rounded-xl border border-gray-100 shadow-sm space-y-3">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <!-- Filter: Employee -->
        <div>
          <label class="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Empleado</label>
          <select
            v-model="localFilters.employee_id"
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @change="onFilterChange"
          >
            <option value="all">Todos los empleados</option>
            <option v-for="emp in allActiveEmployees" :key="emp.id" :value="emp.id">
              {{ emp.name }}
            </option>
          </select>
        </div>

        <!-- Filter: Status -->
        <div>
          <label class="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Estado</label>
          <select
            v-model="localFilters.status"
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @change="onFilterChange"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Solo Pendientes de Pago</option>
            <option value="paid">Solo Liquidados / Pagados</option>
          </select>
        </div>

        <!-- Filter: Event / Folder -->
        <div>
          <label class="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Evento / Proyecto</label>
          <select
            v-if="events && events.length > 0"
            v-model="localFilters.event_name"
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @change="onFilterChange"
          >
            <option value="all">Todos los eventos</option>
            <option v-for="ev in events" :key="ev" :value="ev">{{ ev }}</option>
          </select>
          <input
            v-else
            v-model="localFilters.event_name"
            type="text"
            placeholder="Buscar evento..."
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @change="onFilterChange"
          />
        </div>

        <!-- Filter: Date From -->
        <div>
          <label class="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Fecha Desde</label>
          <input
            v-model="localFilters.date_from"
            type="date"
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @change="onFilterChange"
          />
        </div>

        <!-- Filter: Date To -->
        <div>
          <label class="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Fecha Hasta</label>
          <input
            v-model="localFilters.date_to"
            type="date"
            class="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @change="onFilterChange"
          />
        </div>

        <!-- Actions & Reset -->
        <div class="flex items-end gap-2">
          <button
            type="button"
            class="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors w-full cursor-pointer"
            @click="resetFilters"
          >
            Limpiar
          </button>
        </div>
      </div>
    </div>

    <!-- Shifts Table -->
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-sm">
          <thead>
            <tr class="bg-gray-50/70 border-b border-gray-200/80 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
              <th class="py-3 px-4">Fecha</th>
              <th class="py-3 px-4">Personal</th>
              <th class="py-3 px-4">Tipo de Turno / Labor</th>
              <th class="py-3 px-4">Horas</th>
              <th class="py-3 px-4">Monto ($)</th>
              <th class="py-3 px-4">Estado</th>
              <th class="py-3 px-4">Notas</th>
              <th class="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-if="!shifts?.data || shifts.data.length === 0">
              <td colspan="8" class="py-12 text-center text-gray-400">
                <i class="fa fa-calendar-times text-3xl mb-2 text-gray-300" />
                <p class="font-medium">No se encontraron turnos con los filtros aplicados.</p>
              </td>
            </tr>
            <tr 
              v-for="shift in shifts?.data || []" 
              :key="shift.id"
              class="hover:bg-orange-50/20 transition-colors"
            >
              <!-- Date -->
              <td class="py-3 px-4 font-semibold text-gray-900 whitespace-nowrap">
                {{ formatDate(shift.date) }}
              </td>

              <!-- Employee -->
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ shift.employee?.name }}</div>
                <div class="text-xs text-gray-500">{{ shift.employee?.role_or_job || 'Personal' }}</div>
              </td>

              <!-- Shift Type & Event -->
              <td class="py-3 px-4">
                <div class="flex flex-col gap-1 items-start">
                  <span class="px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700">
                    {{ shift.shift_type }}
                  </span>
                  <span v-if="shift.event_name" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-[10px] font-semibold border border-purple-200">
                    <i class="fa fa-folder-open text-[9px]" />
                    {{ shift.event_name }}
                  </span>
                </div>
              </td>

              <!-- Hours -->
              <td class="py-3 px-4 text-gray-600">
                {{ shift.hours ? shift.hours + ' hrs' : '-' }}
              </td>

              <!-- Amount -->
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ formatUSD(shift.amount) }}</div>
                <div class="text-[10px] text-gray-400">{{ formatBs(shift.amount) }}</div>
              </td>

              <!-- Status -->
              <td class="py-3 px-4">
                <span
                  v-if="shift.status === 'pending'"
                  class="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1.5 w-max"
                >
                  <i class="fa fa-clock text-[10px]" />
                  Pendiente
                </span>
                <span
                  v-else
                  class="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1.5 w-max"
                  :title="`Liquidado en #LIQ-${shift.payroll_payment_id}`"
                >
                  <i class="fa fa-check-circle text-[10px]" />
                  Liquidado
                </span>
              </td>

              <!-- Notes -->
              <td class="py-3 px-4 text-xs text-gray-500 max-w-xs truncate">
                {{ shift.notes || '-' }}
              </td>

              <!-- Actions -->
              <td class="py-3 px-4 text-right">
                <div class="flex items-center justify-end gap-1.5">
                  <button
                    v-if="shift.status === 'pending'"
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                    title="Editar turno"
                    @click="$emit('open-single-shift-modal', shift.employee, shift)"
                  >
                    <i class="fa fa-edit text-sm" />
                  </button>

                  <button
                    v-if="shift.status === 'pending'"
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    title="Eliminar turno"
                    @click="$emit('delete-shift', shift)"
                  >
                    <i class="fa fa-trash-alt text-sm" />
                  </button>

                  <span v-else class="text-[11px] text-gray-400 font-mono italic">
                    Pagado
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="shifts?.links" class="p-4 border-t border-gray-100 flex justify-end">
        <Pagination :links="shifts.links" :query-params="{ tab: 'shifts', ...localFilters }" />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/Shared/Pagination'

export default {
  name: 'PaysheetShiftsTab',

  components: {
    Pagination,
  },

  props: {
    shifts: {
      type: Object,
      required: true,
    },
    allActiveEmployees: {
      type: Array,
      default: () => [],
    },
    events: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
  },

  emits: [
    'filter',
    'reset-filters',
    'open-single-shift-modal',
    'delete-shift',
  ],

  data() {
    return {
      localFilters: {
        employee_id: this.filters?.employee_id || 'all',
        status: this.filters?.status || 'all',
        event_name: this.filters?.event_name || 'all',
        date_from: this.filters?.date_from || '',
        date_to: this.filters?.date_to || '',
      },
    }
  },

  methods: {
    onFilterChange() {
      this.$emit('filter', { ...this.localFilters })
    },

    resetFilters() {
      this.localFilters = {
        employee_id: 'all',
        status: 'all',
        event_name: 'all',
        date_from: '',
        date_to: '',
      }
      this.$emit('reset-filters')
    },

    formatUSD(val) {
      const num = parseFloat(val) || 0
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatBs(val) {
      const num = (parseFloat(val) || 0) * (this.currentExchangeRate || 1)
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Bs.'
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const parts = dateStr.split('T')[0].split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    },
  },
}
</script>
